import { localizeWithValues } from '@/core/gambits';
import router from '@/router';
import { useFilterStore } from '@/stores/filter.store';
import { useTableStore } from '@/stores/table.store';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamErrorInfoSize } from '@/views/composables/constants/components/gamErrorInfo.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import type { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { GamErrorInfoPayload, GamErrorInfoType } from '@/views/composables/models/components/GamErrorInfo';
import { storeToRefs } from 'pinia';
import { RouterNameType } from '../data/config/uiConfig.interface';
import type { GeoCoords } from '../data/location/location.interface';

interface ListOptions {
  id: GamListId;
  title: string;
  message?: string;
  label?: string;
  onClick?: () => void;
}

interface NearbyListOptions extends ListOptions {
  currentLocation?: GeoCoords;
  currentZoom: number;
}

export const listInfo = (options: ListOptions): GamErrorInfoType => ({
  id: options.id,
  title: options.title,
  message: options.message,
  actionPrimary: options.label
    ? {
        label: options.label,
        onClick: options.onClick,
        variant: GamButtonVariant.SLAB,
        size: GamButtonSize.SMALL,
      }
    : undefined,
  size: GamErrorInfoSize.SMALL,
});

export const isValidLocation = (location?: GeoCoords): boolean => {
  return typeof location?.lat === 'number' && typeof location?.lng === 'number';
};

export const formatLatLng = (location: GeoCoords): string => {
  return `${location.lat.toFixed(1)}°N, ${location.lng.toFixed(1)}°E`;
};

export const nearbyListInfo = ({ currentLocation, currentZoom, ...options }: NearbyListOptions): GamErrorInfoType =>
  listInfo({
    id: options.id,
    title: options.title,
    message:
      'Showing results near ' +
      (isValidLocation(currentLocation) ? formatLatLng(currentLocation!) : 'you') +
      ' —  the area you chose on the map.',
    label: 'Change coordinates',
    onClick: () => {
      router.push({
        name: RouterNameType.LOCATIONS,
        query: { ...currentLocation, zoom: Number(currentZoom) - 1 },
      });
    },
  });

export default class ListHelper {
  static getListEnd(payload: GamErrorInfoPayload): GamErrorInfoType {
    const filterStore = useFilterStore(payload.id)();
    const isFilter: boolean = filterStore.isFiltersApplied();

    return {
      id: payload.id,
      title: payload.title || 'app.component.list.end.title',
      message: isFilter ? payload.message || 'app.component.list.end.description' : undefined,
      size: GamErrorInfoSize.SMALL,
      actionSecondary:
        isFilter && payload.clearFilters
          ? {
              variant: GamButtonVariant.SECONDARY,
              label: 'app.component.list.end.button.label',
              onClick: () => {
                useFilterStore(payload.id)().setFilters();
              },
            }
          : undefined,
    };
  }

  static getListNoData(payload: GamErrorInfoPayload): GamErrorInfoType {
    const { tableParams } = storeToRefs(useTableStore(payload.id)());
    const filterStore = useFilterStore(payload.id)();
    const isFilter: boolean = filterStore.isFiltersApplied();

    if (tableParams.value.search?.length) {
      return {
        id: payload.id,
        title: payload.title || 'app.error.title',
        message: localizeWithValues(payload.message || 'app.component.list.notFound.description', [
          tableParams.value.search || '',
        ]),
        size: GamErrorInfoSize.MEDIUM,
        loadIcon: GamIconName.SEARCH,
      };
    } else {
      return {
        id: payload.id,
        title: payload.title || 'app.error.title',
        message: isFilter
          ? payload.message || 'app.component.list.noItems.description'
          : 'app.component.list.noItems.init',
        size: GamErrorInfoSize.MEDIUM,
        actionSecondary:
          isFilter && payload.clearFilters
            ? {
                variant: GamButtonVariant.PRIMARY,
                label: 'app.component.list.noItems.button.clear',
                onClick: () => {
                  useFilterStore(payload.id)().setFilters();
                },
              }
            : undefined,
      };
    }
  }
}
